import { FileDownload, Search } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import DataTable from "react-data-table-component";
import { useCallback, useMemo, useState } from "react";
import { FormSummaryModal } from "../components/dialogs/FormSummaryModal";
import {
  SoulDataModel,
  SoulQuestionnaireForm,
  SoulSubmissionSource,
} from "../data/models/Soul/SoulDataModel";
import { CreateEmailPlanPreviewModal } from "./components/CreateEmailPlanPreviewModal";
import { createUsersColumns } from "./components/AdminUsersPage.columns";
import { useAdminPlansPage } from "./hooks/useAdminPlansPage";
import { useAdminUsers } from "./hooks/useAdminUsers";

export default function AdminUsersPage() {
  const {
    users,
    loading,
    deleting,
    search,
    setSearch,
    handleExportCSV,
    deleteAdminUser,
    fetchUsers,
  } = useAdminUsers();
  const { plans } = useAdminPlansPage();
  const [openCreatePreview, setOpenCreatePreview] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState<any>(null);
  const [selectedSchema, setSelectedSchema] = useState<any>(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);
  const [selectedSubmissionSource, setSelectedSubmissionSource] =
    useState<SoulSubmissionSource | null>(null);
  const [formOptionsOpen, setFormOptionsOpen] = useState(false);
  const [formOptions, setFormOptions] = useState<SoulQuestionnaireForm[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SoulDataModel | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);

  const openFormSummary = useCallback((form: SoulQuestionnaireForm) => {
    const schema = form.latestForm?.json;
    const submission = form.submission?.json;
    const submissionId = form.submission?.id;

    if (!schema || !submission || !submissionId) {
      return;
    }

    setSelectedSchema(schema);
    setSelectedForm(submission);
    setSelectedSchemaId(submissionId);
    setSelectedSubmissionSource(form.submission?.source ?? null);
    setFormOptionsOpen(false);
    setOpenModal(true);
  }, []);

  const handleViewForm = useCallback(
    (user: SoulDataModel) => {
      const questionnaireForms = user.questionnaireForms ?? [];
      const legacyForm: SoulQuestionnaireForm[] =
        user.latestForm?.json && user.submission?.json && user.submission?.id
          ? [
              {
                questionnaire: {
                  id: user.latestForm.id,
                  code: "FORMULARIO",
                  name: "Formulario",
                },
                latestForm: user.latestForm,
                submission: user.submission,
              },
            ]
          : [];
      const forms: SoulQuestionnaireForm[] =
        questionnaireForms.length > 0 ? questionnaireForms : legacyForm;
      const viewableForms = forms.filter(
        (item) =>
          item.latestForm?.json && item.submission?.json && item.submission?.id,
      );

      if (forms.length === 0 || viewableForms.length === 0) {
        return;
      }

      if (forms.length === 1 && viewableForms.length === 1) {
        openFormSummary(viewableForms[0]);
        return;
      }

      setFormOptions(forms);
      setFormOptionsOpen(true);
    },
    [openFormSummary],
  );

  const getUserFormStatus = useCallback((user: SoulDataModel) => {
    const forms = user.questionnaireForms ?? [];
    const submittedForms = forms.filter((item) => item.submission?.json);
    const legacyStarted = Boolean(user.submission?.json);
    const startedCount = submittedForms.length + (legacyStarted ? 1 : 0);
    const finishedCount =
      submittedForms.filter(
        (item) =>
          item.submission?.finishedAt ||
          item.submission?.status === "COMPLETED",
      ).length +
      (user.submission?.finishedAt || user.submission?.status === "COMPLETED"
        ? 1
        : 0);

    if (startedCount === 0) {
      return "No iniciado";
    }

    if (finishedCount === startedCount) {
      return startedCount > 1 ? `${startedCount} finalizados` : "Finalizado";
    }

    return startedCount > 1 ? `${startedCount} en proceso` : "En proceso";
  }, []);

  const openDeleteDialog = useCallback((user: SoulDataModel) => {
    setDeleteTarget(user);
    setDeleteConfirmation("");
    setDeleteError(null);
    setDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    if (deleting) {
      return;
    }
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
    setDeleteConfirmation("");
    setDeleteError(null);
  }, [deleting]);

  const handleDeleteTarget = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }

    const requiresDoubleConfirmation =
      deleteTarget.recordType !== "email_preview";
    if (requiresDoubleConfirmation && deleteConfirmation !== "ELIMINAR") {
      setDeleteError('Escribe "ELIMINAR" para confirmar esta accion.');
      return;
    }

    try {
      setDeleteError(null);
      await deleteAdminUser(deleteTarget, requiresDoubleConfirmation);
      setDeleteSuccess(
        deleteTarget.recordType === "email_preview"
          ? "Pre-registro eliminado correctamente."
          : "Usuario eliminado correctamente.",
      );
      closeDeleteDialog();
      window.setTimeout(() => setDeleteSuccess(null), 4000);
    } catch (error: any) {
      setDeleteError(error?.message || "No se pudo eliminar el usuario.");
    }
  }, [closeDeleteDialog, deleteAdminUser, deleteConfirmation, deleteTarget]);

  const columns = useMemo(
    () =>
      createUsersColumns({
        onViewForm: handleViewForm,
        onDeleteUser: openDeleteDialog,
      }),
    [handleViewForm, openDeleteDialog],
  );

  const deleteRequiresDoubleConfirmation =
    deleteTarget?.recordType !== "email_preview";
  const deleteFormStatus = deleteTarget
    ? getUserFormStatus(deleteTarget)
    : "No iniciado";

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        margin: "24px",
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          {deleteSuccess && <Alert severity="success">{deleteSuccess}</Alert>}

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h4" fontWeight={800} color="text.primary">
                Usuarios
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visualiza usuarios, asigna planes y revisa formularios desde un
                solo lugar.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                onClick={() => setOpenCreatePreview(true)}
                sx={{
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.1,
                  boxShadow: `0 16px 28px ${alpha("#032B66", 0.22)}`,
                }}
              >
                Asignar plan
              </Button>
              <Button
                startIcon={<FileDownload />}
                variant="outlined"
                size="large"
                onClick={handleExportCSV}
                sx={{
                  borderRadius: 3,
                  px: 2.5,
                  py: 1.1,
                }}
              >
                Exportar CSV
              </Button>
            </Stack>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 4,
              border: "1px solid rgba(15, 23, 42, 0.08)",
              bgcolor: "rgba(255,255,255,0.82)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Stack spacing={2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
              >
                <TextField
                  size="small"
                  placeholder="Buscar por nombre, correo, pasaporte o plan..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  sx={{ maxWidth: { md: 560 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />

                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`${users.length} usuarios`}
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                  />
                  <Chip
                    label={`${plans.length} planes`}
                    variant="outlined"
                    sx={{ borderRadius: 999 }}
                  />
                </Stack>
              </Stack>

              <Typography variant="body2" color="text.secondary">
                Filtra y navega el listado sin perder de vista el contexto de
                planes asociados.
              </Typography>
            </Stack>
          </Paper>

          {loading ? (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <DataTable
              keyField="rowKey"
              columns={columns}
              data={users}
              pagination
              paginationResetDefaultPage
              paginationPerPage={10}
              paginationRowsPerPageOptions={[10, 20, 50]}
              paginationComponentOptions={{
                rowsPerPageText: "Registros por pagina",
                rangeSeparatorText: "de",
              }}
              highlightOnHover
              striped
              responsive
              persistTableHead
              pointerOnHover
              noDataComponent="No hay usuarios para mostrar"
              customStyles={{
                table: {
                  style: { width: "100%" },
                },
                headCells: {
                  style: {
                    fontWeight: 700,
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                  },
                },
                cells: {
                  style: {
                    fontSize: "14px",
                  },
                },
                rows: {
                  style: {
                    minHeight: "54px",
                  },
                },
              }}
            />
          )}
        </Stack>
      </CardContent>

      {selectedForm && selectedSchema && selectedSchemaId && (
        <FormSummaryModal
          open={openModal}
          onClose={async () => {
            setOpenModal(false);
            setSelectedForm(null);
            setSelectedSchema(null);
            setSelectedSchemaId(null);
            setSelectedSubmissionSource(null);
            await fetchUsers();
          }}
          schema={selectedSchema}
          submission={selectedForm}
          submissionId={selectedSchemaId}
          submissionSource={selectedSubmissionSource ?? undefined}
        />
      )}

      <Dialog
        open={formOptionsOpen}
        onClose={() => setFormOptionsOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Seleccionar formulario
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <List disablePadding>
            {formOptions.map((item, index) => {
              const canOpen =
                Boolean(item.latestForm?.json) &&
                Boolean(item.submission?.json) &&
                Boolean(item.submission?.id);
              const status =
                item.submission?.finishedAt ||
                item.submission?.status === "COMPLETED"
                  ? "Finalizado"
                  : item.submission?.json
                    ? "En proceso"
                    : "No iniciado";

              return (
                <Box
                  key={`${item.questionnaire.code}-${item.submission?.id ?? index}`}
                >
                  <ListItemButton
                    disabled={!canOpen}
                    onClick={() => openFormSummary(item)}
                    sx={{ py: 1.5, px: 3 }}
                  >
                    <ListItemText
                      primary={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          flexWrap="wrap"
                        >
                          <Typography fontWeight={700}>
                            {item.questionnaire.name || item.questionnaire.code}
                          </Typography>
                          <Chip
                            label={status}
                            size="small"
                            color={
                              status === "Finalizado"
                                ? "success"
                                : status === "En proceso"
                                  ? "warning"
                                  : "default"
                            }
                            sx={{ fontWeight: 700 }}
                          />
                        </Stack>
                      }
                      secondary={
                        item.questionnaire.description ||
                        item.questionnaire.code ||
                        "Formulario asignado"
                      }
                    />
                  </ListItemButton>
                  {index < formOptions.length - 1 && <Divider />}
                </Box>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {deleteRequiresDoubleConfirmation
            ? "Eliminar usuario"
            : "Eliminar pre-registro"}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {deleteError && <Alert severity="error">{deleteError}</Alert>}
            <Alert
              severity={deleteRequiresDoubleConfirmation ? "warning" : "info"}
            >
              Esta accion guardara un respaldo JSON antes de eliminar el
              registro y liberar el correo.
            </Alert>
            <Stack spacing={0.5}>
              <Typography>
                <strong>Nombre:</strong> {deleteTarget?.fullName || "Anonimo"}
              </Typography>
              <Typography>
                <strong>Correo:</strong> {deleteTarget?.email || "-"}
              </Typography>
              <Typography>
                <strong>Plan:</strong> {deleteTarget?.plan?.name || "Sin plan"}
              </Typography>
              <Typography>
                <strong>Formulario:</strong> {deleteFormStatus}
              </Typography>
            </Stack>
            {deleteRequiresDoubleConfirmation && (
              <TextField
                label='Escribe "ELIMINAR" para confirmar'
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                disabled={deleting}
                fullWidth
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteTarget}
            disabled={
              deleting ||
              (deleteRequiresDoubleConfirmation &&
                deleteConfirmation !== "ELIMINAR")
            }
          >
            {deleting ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateEmailPlanPreviewModal
        open={openCreatePreview}
        onClose={async () => {
          setOpenCreatePreview(false);
          await fetchUsers();
        }}
        users={users}
        plans={plans}
      />
    </Card>
  );
}
